import { useCallback, useEffect, useRef, useState } from 'react'
import { useRoute } from 'wouter'
import API from '@enums/api'
import { PodiumDetail } from '@types'

/**
 * The detail page's device data: one fetch of `GET /v2/podium/:hardwareId`, plus a
 * `reload` the action buttons call after they change something server-side (disable,
 * wipe, retry) so the header and tiles catch up without a page refresh.
 *
 * The jobs and events cards own their own paging and filtering — they fetch
 * independently rather than being threaded through here, so a Load-more in one can't
 * re-render the other.
 */
export default function usePodiumPage() {
    const [match, params] = useRoute('/podiums/:hardwareId')
    const hardwareId = match ? params.hardwareId : null

    const [podium, setPodium] = useState<PodiumDetail | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    // Monotonic request id, checked after the await so only the latest fetch commits
    // state — a reload triggered by an action can overlap the one before it.
    const latestRequest = useRef(0)

    const load = useCallback(async () => {
        if (!hardwareId) {
            setLoading(false)
            setError('No podium specified.')
            return
        }

        const requestId = latestRequest.current + 1
        latestRequest.current = requestId
        setError(null)

        const res = await API.ha.podium.getDetail(hardwareId).catch(() => null)
        if (requestId !== latestRequest.current) return

        setLoading(false)
        if (!res?.ok || !res.value) {
            setError("Couldn't load this podium.")
            return
        }
        setPodium(res.value)
    }, [hardwareId])

    useEffect(() => {
        setLoading(true)
        setPodium(null)
        void load()
    }, [load])

    return { hardwareId, podium, loading, error, reload: load }
}
